/**
 * Client-side image compression: shrink to a max dimension and re-encode as JPEG/WebP.
 * Cuts upload size (and downstream model token cost) before the bytes leave the browser.
 *
 * Falls back to the original file if anything in the pipeline fails so the user
 * is never blocked by a flaky decoder.
 */

export type CompressOptions = {
  /** Longest side in pixels — image is scaled down (never up) to fit. */
  maxDimension?: number;
  /** JPEG/WebP quality 0..1 (ignored for transparent → WebP). */
  quality?: number;
  /** Output format. "auto" keeps PNG transparency as WebP, otherwise JPEG. */
  format?: 'auto' | 'jpeg' | 'webp';
};

const DEFAULTS: Required<CompressOptions> = {
  maxDimension: 1536,
  quality: 0.85,
  format: 'auto',
};

function loadImageBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file).catch(() => loadImageElement(file));
  }
  return loadImageElement(file);
}

function loadImageElement(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not decode image.'));
    };
    img.src = url;
  });
}

function readDimensions(source: ImageBitmap | HTMLImageElement) {
  if (source instanceof HTMLImageElement) {
    return { width: source.naturalWidth, height: source.naturalHeight };
  }
  return { width: source.width, height: source.height };
}

export async function compressImage(file: File, opts: CompressOptions = {}): Promise<File> {
  if (typeof window === 'undefined' || typeof document === 'undefined') return file;
  if (!file.type.startsWith('image/')) return file;
  // SVG / GIF are kept as-is — re-encoding loses animation / vector data.
  if (file.type === 'image/svg+xml' || file.type === 'image/gif') return file;

  const { maxDimension, quality, format } = { ...DEFAULTS, ...opts };

  try {
    const source = await loadImageBitmap(file);
    const { width: srcW, height: srcH } = readDimensions(source);
    if (!srcW || !srcH) return file;

    const scale = Math.min(1, maxDimension / Math.max(srcW, srcH));
    const targetW = Math.max(1, Math.round(srcW * scale));
    const targetH = Math.max(1, Math.round(srcH * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;

    // Preserve transparency for PNG/WebP sources by leaving the canvas clear.
    const keepAlpha = file.type === 'image/png' || file.type === 'image/webp';
    if (!keepAlpha) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, targetW, targetH);
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(source, 0, 0, targetW, targetH);

    const outFormat =
      format === 'auto' ? (keepAlpha ? 'image/webp' : 'image/jpeg') : `image/${format}`;
    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), outFormat, quality)
    );
    if (!blob) return file;

    // If the round-trip somehow grew the file (rare for tiny icons that re-encode badly),
    // keep the original.
    if (blob.size >= file.size && srcW <= maxDimension && srcH <= maxDimension) {
      return file;
    }

    const ext = outFormat === 'image/webp' ? 'webp' : 'jpg';
    const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
    return new File([blob], `${baseName}.${ext}`, {
      type: outFormat,
      lastModified: Date.now(),
    });
  } catch {
    return file;
  }
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
